/**
 * useHotspotPocArchive — Story 1.5 (PoC-Historienarchiv, HIP-149)
 *
 * GET /api/v2/hotspots/{id}/poc_archive — eingefrorene PoC-Kennzahlen
 * (Bestandsdaten bis Juli 2026, keine Live-Oracle-Anbindung).
 *
 * downloadPocArchiveCsv: GET /api/v2/hotspots/{id}/poc_archive/export als Blob
 * (text/csv) und clientseitiger Download über ein temporäres <a download>.
 */
import { useApi } from '../../lib/use-api'
import apiClient from '../../lib/api-client'
import type { components } from '@heliumtracker/api-types'

export type HotspotPocArchive = components['schemas']['HotspotPocArchive']

export type UsePocArchiveResult = {
  data: HotspotPocArchive | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

export function useHotspotPocArchive(id: string, enabled = true): UsePocArchiveResult {
  const query = useApi<HotspotPocArchive>(
    ['hotspot', id, 'poc-archive'],
    async () => {
      const result = await apiClient.GET('/api/v2/hotspots/{id}/poc_archive', {
        params: { path: { id } },
      })
      if (result.error) return { error: result.error }
      return { data: result.data?.data }
    },
    // Archivdaten sind eingefroren (HIP-149) — aggressiv cachen.
    { enabled, staleTime: Infinity },
  )
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => query.refetch(),
  }
}

/**
 * Lädt den CSV-Export herunter. Wirft bei HTTP-/Netzwerkfehlern —
 * der Aufrufer zeigt darauf einen Fehler-Toast.
 */
export async function downloadPocArchiveCsv(id: string, fileName?: string): Promise<void> {
  const result = await apiClient.GET('/api/v2/hotspots/{id}/poc_archive/export', {
    params: { path: { id } },
    parseAs: 'blob',
  })
  if (result.error || !result.data) {
    throw new Error('poc_archive_export_failed')
  }
  const blob = result.data as Blob
  const url = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = fileName ?? `poc-archive-${id}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
  } finally {
    URL.revokeObjectURL(url)
  }
}
